import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Music, Check, Instagram, Facebook, Twitter } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  name: "", email: "", phone: "", song_request: "", artist: "", dedication: "",
  instagram: "", facebook: "", tiktok: "", twitter: "",
};

const fade = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-[0.2em] text-[#A19D94]">{label}</span>
    {children}
  </label>
);

const inputCls =
  "mt-2 w-full bg-transparent border-b border-white/20 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors duration-300";

export const RequestForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.song_request) {
      toast.error("Please fill in your name, email and a song request.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", form);
      setDone(true);
      toast.success("Request received — see you on the dancefloor.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request" className="relative py-24 md:py-32 bg-[#150505]" data-testid="request-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37] mb-6">Score The Night</p>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-[#FDFBF7]">
            Request a <span className="italic text-gradient-gold">Song</span>
          </h2>
          <p className="mt-6 text-[#A19D94] max-w-xl mx-auto">
            Tell us what you need to hear. Drop your details and socials so Jay can keep you close for every event to come.
          </p>
        </motion.div>

        <motion.div variants={fade} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="border border-white/10 bg-black/40 backdrop-blur-xl p-8 md:p-12 gold-glow">
          {done ? (
            <div className="text-center py-16" data-testid="request-success">
              <div className="w-16 h-16 rounded-full border border-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                <Check className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-serif-display text-3xl text-[#FDFBF7]">You're on the list.</h3>
              <p className="text-[#A19D94] mt-3">Your request landed. Jay's got it from here.</p>
              <button onClick={() => setDone(false)} data-testid="request-another-button"
                className="mt-8 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-8 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300">
                Request Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-10" data-testid="request-form">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Music className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">The Request</span>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Song title *">
                    <input data-testid="input-song" className={inputCls} value={form.song_request} onChange={set("song_request")} placeholder="e.g. Untitled (How Does It Feel)" />
                  </Field>
                  <Field label="Artist">
                    <input data-testid="input-artist" className={inputCls} value={form.artist} onChange={set("artist")} placeholder="e.g. D'Angelo" />
                  </Field>
                </div>
                <div className="mt-8">
                  <Field label="Dedication / note (optional)">
                    <input data-testid="input-dedication" className={inputCls} value={form.dedication} onChange={set("dedication")} placeholder="Shout out someone special…" />
                  </Field>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Your Details</span>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Full name *">
                    <input data-testid="input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                  </Field>
                  <Field label="Phone / SMS">
                    <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000" />
                  </Field>
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Socials (optional)</span>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <Field label="Instagram">
                    <div className="flex items-center"><Instagram className="w-4 h-4 text-[#8C7324] mr-2 mt-2 self-end mb-3" />
                      <input data-testid="input-instagram" className={inputCls} value={form.instagram} onChange={set("instagram")} placeholder="@handle" /></div>
                  </Field>
                  <Field label="Facebook">
                    <div className="flex items-center"><Facebook className="w-4 h-4 text-[#8C7324] mr-2 mt-2 self-end mb-3" />
                      <input data-testid="input-facebook" className={inputCls} value={form.facebook} onChange={set("facebook")} placeholder="facebook.com/you" /></div>
                  </Field>
                  <Field label="TikTok">
                    <div className="flex items-center"><span className="text-[#8C7324] mr-2 mb-3 self-end text-sm">♪</span>
                      <input data-testid="input-tiktok" className={inputCls} value={form.tiktok} onChange={set("tiktok")} placeholder="@handle" /></div>
                  </Field>
                  <Field label="X / Twitter">
                    <div className="flex items-center"><Twitter className="w-4 h-4 text-[#8C7324] mr-2 mt-2 self-end mb-3" />
                      <input data-testid="input-twitter" className={inputCls} value={form.twitter} onChange={set("twitter")} placeholder="@handle" /></div>
                  </Field>
                </div>
              </div>

              <button type="submit" disabled={loading} data-testid="submit-request-button"
                className="w-full md:w-auto text-xs uppercase tracking-[0.25em] border border-[#D4AF37] text-[#D4AF37] px-12 py-4 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 disabled:opacity-50">
                {loading ? "Sending…" : "Send My Request"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
